/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useMemo, useContext, type RefObject } from 'react';
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { useNavigationView } from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import type { IconProps } from '@atlaskit/icon/types';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { WithDragPreview } from '../../../drag-preview/index.tsx';
import { EditableTitle } from '../../../editable-title/index.tsx';
import { ViewIcon } from '../../../title-icon-resolver/index.tsx';
import { DraggableRefContext } from '../../../../common/utils/draggable-context/index.tsx';
import { useAnalyticsL2, useContainerId } from '../../../../controllers/context.tsx';
import type { NavigationContainer } from '../../../../common/types.tsx';
import { Menu } from './menu/index.tsx';

const defaultTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.default-title',

	defaultMessage: 'Untitled Section',
	description: 'Header text to be used as a default when a section title changes to empty',
});

type ViewSetProps = {
	localId: LocalViewId;
	children?: React.ReactNode;
	navigationContainer: NavigationContainer;
	isDraggingPreview?: boolean;
	isReadOnly: boolean;
	isExpanded: boolean;
	onToggle?: (localId: LocalViewId) => void;
};

type ExpandableMenuItemIconProps = {
	isExpanded: boolean;
	iconProps?: IconProps;
};

const nameLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.rename-label',
	defaultMessage: 'View name',
	description: 'Accessibility label display for input when putting view name in navigation',
});

const ExpandableMenuItemIcon = ({
	iconProps,
	isExpanded,
}: ExpandableMenuItemIconProps): JSX.Element => {
	const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	return <ChevronIcon {...iconProps} label={isExpanded ? 'Collapse' : 'Expand'} size="small" />;
};

export const ViewSet = ({
	isDraggingPreview,
	localId,
	navigationContainer,
	isExpanded,
	isReadOnly,
	onToggle,
}: ViewSetProps): React.JSX.Element | undefined => {
	const { formatMessage } = useIntl();
	const containerId = useContainerId();
	const [view] = useNavigationView({ localId, containerId });
	const { sendViewNavigationItemInsideProjectsAnalyticsEvent } = useAnalyticsL2();
	const { deleteViewSet, createViewSet, updateViewSet, cancelViewSetRenaming } =
		useNavigationActions();
	const isNew = view?.name === '';

	const draggableRef = useContext(DraggableRefContext);

	const handleTitleChanged = useCallback(
		(newTitle: string) => {
			if (!view) {
				return;
			}
			if (isNew) {
				createViewSet(view.localId, newTitle || formatMessage(defaultTitle));
			} else {
				updateViewSet(view.localId, newTitle || formatMessage(defaultTitle));
			}
		},
		[view, isNew, createViewSet, formatMessage, updateViewSet],
	);

	const handleTitleChangeCanceled = useCallback(() => {
		if (!view) {
			return;
		}
		if (isNew) {
			deleteViewSet(view.localId);
			return;
		}
		cancelViewSetRenaming(view.localId);
	}, [view, isNew, cancelViewSetRenaming, deleteViewSet]);

	const handleToggle = useCallback(() => {
		onToggle?.(localId);
		sendViewNavigationItemInsideProjectsAnalyticsEvent({
			container: navigationContainer,
			viewParams: {
				viewId: view?.id,
				viewKind: view?.viewType,
			},
		});
	}, [
		onToggle,
		localId,
		sendViewNavigationItemInsideProjectsAnalyticsEvent,
		navigationContainer,
		view?.id,
		view?.viewType,
	]);

	const expandableElemBefore = useMemo(
		() => <ExpandableMenuItemIcon aria-expanded={isExpanded} isExpanded={isExpanded} />,
		[isExpanded],
	);

	if (!view) {
		return;
	}

	if (isNew) {
		return (
			<MenuListItem>
				<EditableTitle
					label={formatMessage(nameLabel)}
					value={view.name}
					elemBefore={<ViewIcon viewKind={view.viewType} />}
					onChanged={handleTitleChanged}
					onCancel={handleTitleChangeCanceled}
				/>
			</MenuListItem>
		);
	}

	if (view.isRenaming) {
		return (
			<MenuListItem>
				<EditableTitle
					label={formatMessage(nameLabel)}
					value={view.name}
					elemBefore={<ViewIcon viewKind={view.viewType} />}
					onChanged={handleTitleChanged}
					onCancel={handleTitleChangeCanceled}
				/>
			</MenuListItem>
		);
	}

	return (
		<WithDragPreview isEnabled={isDraggingPreview || false}>
			<MenuList>
				<ButtonMenuItem
					// oxlint-disable-next-line typescript/consistent-type-assertions
					ref={draggableRef as RefObject<HTMLButtonElement>}
					onClick={handleToggle}
					elemBefore={expandableElemBefore}
					actionsOnHover={
						!isReadOnly && <Menu view={view} navigationContainer={navigationContainer} />
					}
				>
					{view.name}
				</ButtonMenuItem>
			</MenuList>
		</WithDragPreview>
	);
};
